"use client";

import { cva } from "class-variance-authority";
import useEmblaCarousel from "embla-carousel-react";
import { Children, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Scrollbar } from "@/components/ui/Scrollbar";

export type CarouselLayout = "fill" | "quartets" | "triplets" | "pair";

interface CarouselTrackProps {
	children: ReactNode;
	layout?: CarouselLayout;
	desktopAlwaysCarousel?: boolean;
	className?: string;
	darkMode?: boolean;
	/**
	 * Optional slot rendered between the embla track and the scrollbar — used
	 * by the cart upsell to inject an inline quick-add panel directly under
	 * the carousel while keeping the slider indicator below it.
	 */
	slotBeforeScrollbar?: ReactNode;
}

const containerStyles = cva(
	"flex items-start gap-grid-grid-gutter-md px-4 md:px-0 [&>*]:basis-4/5 [&>*]:max-w-80 [&>*]:shrink-0 [&>*]:grow-0 [&>*:last-child]:mr-4 md:[&>*:last-child]:mr-0",
	{
		variants: {
			layout: {
				fill: "md:[&>*]:basis-0 md:[&>*]:max-w-full md:[&>*]:flex-1",
				quartets: "md:[&>*]:basis-1/4 md:[&>*]:max-w-full",
				// Triplets is the only layout that also overrides the mobile peek —
				// the cart upsell wants 3 cards visible on mobile too, not the
				// default 80%-width-with-peek that other carousels use.
				triplets: "[&>*]:basis-1/3! [&>*]:max-w-full! [&>*:last-child]:mr-0!",
				pair: "md:[&>*]:basis-1/2 md:[&>*]:max-w-full",
			},
		},
		defaultVariants: { layout: "fill" },
	},
);

const viewportStyles = cva("w-[calc(100%+2rem)] md:w-full -mx-4 md:mx-0 overflow-hidden", {
	variants: {
		desktopOverflow: {
			visible: "md:overflow-visible",
			hidden: "",
		},
	},
	defaultVariants: { desktopOverflow: "visible" },
});

export const CarouselTrack = ({
	children,
	layout = "fill",
	desktopAlwaysCarousel = false,
	className,
	darkMode,
	slotBeforeScrollbar,
}: CarouselTrackProps) => {
	const slideCount = Children.count(children);
	const needsCarousel = slideCount > 1;
	const emblaActive = needsCarousel && desktopAlwaysCarousel;

	const breakpoints = useMemo(
		() =>
			needsCarousel
				? { "(max-width: 767px)": { active: true, align: "center" as const } }
				: undefined,
		[needsCarousel],
	);

	const [emblaRef, emblaApi] = useEmblaCarousel({
		active: emblaActive,
		align: "start",
		containScroll: "trimSnaps",
		breakpoints,
	});

	const [selectedIndex, setSelectedIndex] = useState(0);
	const [snapCount, setSnapCount] = useState(0);

	useEffect(() => {
		if (!emblaApi) return;
		const sync = () => {
			setSelectedIndex(emblaApi.selectedScrollSnap());
			setSnapCount(emblaApi.scrollSnapList().length);
		};
		emblaApi.on("select", sync);
		emblaApi.on("reInit", sync);
		sync();
		return () => {
			emblaApi.off("select", sync);
			emblaApi.off("reInit", sync);
		};
	}, [emblaApi]);

	const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

	const desktopOverflow = desktopAlwaysCarousel ? "hidden" : "visible";
	const showScrollbar = snapCount > 1;

	return (
		<div className={twMerge("flex w-full flex-col gap-grid-section-vertical-md", className)}>
			<div ref={emblaRef} className={viewportStyles({ desktopOverflow })}>
				<div className={containerStyles({ layout })}>{children}</div>
			</div>
			{slotBeforeScrollbar}
			{showScrollbar && (
				<div className={desktopAlwaysCarousel ? "" : "md:hidden"}>
					<Scrollbar
						current={selectedIndex + 1}
						total={snapCount}
						onPrev={scrollPrev}
						onNext={scrollNext}
						darkMode={darkMode}
					/>
				</div>
			)}
		</div>
	);
};
